import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import { DispatchRiderSnapshot } from '@/services/api/dispatches';
import {
  DispatchDriver,
  dispatchDriversAPI,
  driverDisplayName,
} from '@/services/api/dispatchDrivers';

interface AddRidersModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Riders already on the dispatch — shown as "In dispatch", not addable. */
  existingRiders: DispatchRiderSnapshot[];
  /**
   * Plain hub name (not "code · name") — powers the Recommended tab via
   * the drivers `tagNames` filter. Absent while the hub is still loading.
   */
  hubName?: string;
  onSubmit: (riders: DispatchRiderSnapshot[]) => Promise<void>;
}

/**
 * Append extra driver(s) to a PREVIEW dispatch's frozen snapshot (§D5).
 * No re-plan happens — the new rider starts empty; ops then moves work to
 * them with the existing reassign actions.
 */
const AddRidersModal: React.FC<AddRidersModalProps> = ({
  isOpen,
  onClose,
  existingRiders,
  hubName,
  onSubmit,
}) => {
  const [drivers, setDrivers] = useState<DispatchDriver[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Same Recommended/All source tabs as CreateDispatchModal — Recommended
  // filters by tagNames=<hub name>, All is the unfiltered list.
  const [riderTab, setRiderTab] = useState<'RECOMMENDED' | 'ALL'>('ALL');
  const [selected, setSelected] = useState<
    Record<number, DispatchRiderSnapshot>
  >({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelected({});
    setSearch('');
    setDebouncedSearch('');
  }, [isOpen]);

  // The dispatch's hub is fixed, so recommendations are the natural
  // starting point whenever we know the hub name. Separate from the
  // reset-on-open effect so a late-resolving hub name (async lookup on the
  // parent page) only flips the tab and never wipes an in-progress
  // selection.
  useEffect(() => {
    if (isOpen) setRiderTab(hubName ? 'RECOMMENDED' : 'ALL');
  }, [isOpen, hubName]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch lives in the effect (not a bare callback) so the cleanup flag can
  // discard stale responses: opening the modal flips the tab ALL →
  // RECOMMENDED one render after the first fetch fires, and without
  // cancellation the slower unfiltered response would overwrite the
  // recommended list.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setDriversLoading(true);
    dispatchDriversAPI
      .list({
        active: true,
        search: debouncedSearch || undefined,
        size: 100,
        ...(riderTab === 'RECOMMENDED' && hubName
          ? { tagNames: hubName }
          : {}),
      })
      .then((res) => {
        if (!cancelled) setDrivers(res.data ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setDrivers([]);
        toast.error('Could not load drivers', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        if (!cancelled) setDriversLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedSearch, riderTab, hubName]);

  const existingIds = useMemo(
    () => new Set(existingRiders.map((r) => r.id)),
    [existingRiders]
  );

  const toggleDriver = (d: DispatchDriver) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[d.id]) delete next[d.id];
      else next[d.id] = { id: d.id, code: d.code, name: driverDisplayName(d) };
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;
  const canSubmit = !submitting && selectedCount > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(Object.values(selected));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-user-add-line" />}
      iconTone="primary"
      title="Add riders"
      subtitle="New riders start empty — move batches or stops to them after adding."
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-text-sub-600">
            {selectedCount} rider{selectedCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Adding…' : 'Add to dispatch'}
            </Button>
          </div>
        </div>
      }
    >
      {/* Source tabs — Recommended (drivers tagged with this dispatch's
          hub name) vs All. Recommended needs the hub name resolved. */}
      <div className="flex items-center -mb-px border-b border-stroke-soft-200 mb-2">
        <button
          type="button"
          onClick={() => setRiderTab('RECOMMENDED')}
          disabled={submitting || !hubName}
          title={!hubName ? 'Hub still loading…' : undefined}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors border-b-2 disabled:cursor-not-allowed ${
            riderTab === 'RECOMMENDED'
              ? 'border-accent text-text-strong-950'
              : 'border-transparent text-text-sub-600 hover:text-text-strong-950 disabled:text-text-disabled-300 disabled:hover:text-text-disabled-300'
          }`}
        >
          <i className="ri-sparkling-line text-[13px]" aria-hidden="true" />
          Dedicated Driver
        </button>
        <button
          type="button"
          onClick={() => setRiderTab('ALL')}
          disabled={submitting}
          className={`inline-flex items-center px-3 py-1.5 text-[12px] font-semibold tracking-tight transition-colors border-b-2 ${
            riderTab === 'ALL'
              ? 'border-accent text-text-strong-950'
              : 'border-transparent text-text-sub-600 hover:text-text-strong-950'
          }`}
        >
          All riders
        </button>
      </div>
      <div className="relative mb-2">
        <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-text-soft-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, code, or phone…"
          disabled={submitting}
          className="w-full rounded-lg border border-stroke-soft-200 bg-white px-3 py-2 pl-9 text-sm text-text-strong-950 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:bg-bg-soft-200 disabled:cursor-not-allowed disabled:text-text-sub-600"
        />
      </div>
      <div className="max-h-[360px] min-h-[200px] overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {driversLoading ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            Loading drivers…
          </div>
        ) : drivers.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            {riderTab === 'RECOMMENDED'
              ? search
                ? 'No matches among riders tagged for this hub.'
                : `No riders tagged "${hubName}" yet — check All riders.`
              : search
              ? 'No matches.'
              : 'Type to search drivers.'}
          </div>
        ) : (
          drivers.map((d) => {
            const alreadyIn = existingIds.has(d.id);
            const checked = !!selected[d.id];
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => toggleDriver(d)}
                disabled={submitting || alreadyIn}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                  alreadyIn
                    ? 'bg-bg-weak-50 cursor-default'
                    : checked
                    ? 'bg-bg-weak-50'
                    : 'hover:bg-bg-weak-50 cursor-pointer'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                    {driverDisplayName(d)}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] text-text-sub-600 tabular-nums">
                    <span>{d.code}</span>
                    {d.phone_number && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{d.phone_number}</span>
                      </>
                    )}
                  </span>
                </span>
                {alreadyIn ? (
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-soft-400 shrink-0">
                    In dispatch
                  </span>
                ) : (
                  <i
                    className={`${
                      checked
                        ? 'ri-checkbox-circle-fill text-accent'
                        : 'ri-add-line text-accent'
                    } text-[16px] shrink-0`}
                  />
                )}
              </button>
            );
          })
        )}
      </div>
    </FormModal>
  );
};

export default AddRidersModal;
